import _ from 'lodash'
import {
    Checkbox as CheckboxUI,
    FormControl,
    FormControlLabel,
    FormControlProps,
    FormHelperText,
    InputLabel,
    InputLabelProps,
    OutlinedInput,
    OutlinedInputProps,
} from '@material-ui/core'
import { FieldValidate, validators } from 'src/common/razzle-platform-components/form-validators'
import React, { ChangeEvent, FC, ReactNode } from 'react'
import { RegisterOptions, useFormContext, FormProvider, useForm } from 'react-hook-form'

export type FormProps = {
    defaultValues?: Record<string, unknown>
    onSubmit(model: unknown): void
    onError?(error: unknown): void
}

export const Form: FC<FormProps> = ({ children, defaultValues, onError, onSubmit, ...formProps }): JSX.Element => {
    const methods = useForm({ mode: 'all', ...(defaultValues ? { defaultValues } : null) })
    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit, onError)} noValidate {...formProps}>
                {children}
            </form>
        </FormProvider>
    )
}



export type TextFieldInputType =
    | 'color'
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'month'
    | 'number'
    | 'password'
    | 'search'
    | 'submit'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week'

export interface TextFieldProps {
    type?: TextFieldInputType
    label: string
    name: string
    validate?: FieldValidate
    outlinedInputProps?: OutlinedInputProps
    formControlProps?: FormControlProps
    inputLabelProps?: InputLabelProps
    required?: boolean
    disabled?: boolean
    fullWidth?: boolean
    color?: 'primary' | 'secondary'
    defaultValue?: string | number
    autoComplete?: string
    endAdornment?: ReactNode
}

export const TextField: FC<TextFieldProps> = (props): JSX.Element => {
    const labelRef = React.createRef<HTMLLabelElement>()
    const {
        autoComplete,
        color,
        defaultValue,
        disabled,
        endAdornment,
        formControlProps,
        fullWidth,
        inputLabelProps,
        label,
        name,
        outlinedInputProps,
        required,
        type,
        validate,
    } = props
    const registerOptions: RegisterOptions = {}
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerOptions.validate = _.isArray(validate) ? (validators(validate) as any) : {}

    const extraOutlinedInputProps: Partial<OutlinedInputProps> = {}

    if (typeof inputLabelProps?.shrink !== 'undefined') {
        extraOutlinedInputProps.notched = inputLabelProps.shrink
    }

    if (label) {
        const displayRequired = Boolean(inputLabelProps?.required ?? props.required)
        extraOutlinedInputProps.label = (
            <React.Fragment>
                {label}
                {displayRequired && '\u00a0*'}
            </React.Fragment>
        )
    }
    const { errors, register } = useFormContext()

    return (
        <FormControl
            variant="outlined"
            required={required}
            disabled={disabled}
            fullWidth={fullWidth}
            color={color}
            {...formControlProps}
            error={_.has(errors, name)}
        >
            {label && (
                <InputLabel
                    htmlFor={name}
                    ref={labelRef}
                    required={required}
                    disabled={disabled}
                    color={color}
                    {...props.inputLabelProps}
                >
                    {label}
                </InputLabel>
            )}
            <OutlinedInput
                error={_.has(errors, name)}
                id={name}
                name={name}
                aria-describedby={name}
                required={required}
                disabled={disabled}
                fullWidth={fullWidth}
                color={color}
                defaultValue={defaultValue}
                {...(type ? { type } : null)}
                {...(autoComplete ? { autoComplete } : null)}
                {...(endAdornment ? { endAdornment } : null)}
                {...outlinedInputProps}
                {...extraOutlinedInputProps}
                inputRef={register(registerOptions)}
            />
            {_.has(errors, name) && <FormHelperText id={name}>{_.get(errors, `${name}.message`)}</FormHelperText>}
        </FormControl>
    )
}

export interface CheckboxProps {
    label: string
    name: string
    validate?: FieldValidate
    formControlProps?: FormControlProps
    required?: boolean
    disabled?: boolean
    fullWidth?: boolean
    color?: 'primary' | 'secondary'
    defaultValue?: boolean
}

export const Checkbox: FC<CheckboxProps> = (props): JSX.Element => {
    const { color, disabled, formControlProps, fullWidth, label, name, required, validate } = props
    const registerOptions: RegisterOptions = {}
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerOptions.validate = _.isArray(validate) ? (validators(validate) as any) : {}

    const { errors, register, setValue } = useFormContext()

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setValue(name, event.target.checked)
    }

    React.useEffect(() => {
        register(name, registerOptions)
    }, [register])

    return (
        <FormControl
            required={required}
            disabled={disabled}
            fullWidth={fullWidth}
            color={color}
            {...formControlProps}
            error={_.has(errors, name)}
        >
            <FormControlLabel
                label={label}
                control={<CheckboxUI color="primary" id={name} onChange={handleChange} />}
            />
            {_.has(errors, name) && <FormHelperText id={name}>{_.get(errors, `${name}.message`)}</FormHelperText>}
        </FormControl>
    )
}
